/**
 * ProtectedRoute - Route protection with Wristband authentication
 *
 * @description
 * Protects routes by checking authentication and optional role requirements.
 * Uses user.role directly from the AuthContext (not publicMetadata).
 *
 * @dependencies
 * - AuthContext useUser
 * - BootSplashDismisser: hide HTML boot chrome when DashboardSkeleton commits
 * - BootSplashKeeper: restore chrome on unsigned !isLoaded and on Navigate-to-sign-in
 * - DashboardSkeleton: signed-in LynxPay hydration placeholder
 * - PackResolvingShell: /so hydration — never LynxPay chrome while pack-chrome is unresolved
 *
 * @notes
 * - Unsigned !isLoaded returns BootSplashKeeper so HTML #boot-splash stays up.
 * - Valid session + !isLoaded paints the light skeleton and dismisses chrome
 *   in the same commit.
 * - Unsigned isLoaded Navigate must restore chrome in the same commit — App
 *   still paints SignedOut until /sign-in mounts, and ready=false does not
 *   unhide an already-dismissed overlay.
 */
import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useUser } from '../contexts/AuthContext';
import { hasValidStoredSession } from '../utils/authHelpers';
import DashboardSkeleton from './DashboardSkeleton';
import PackResolvingShell from './layouts/so/PackResolvingShell';
import { isSoAppPath } from '../utils/packChromeHint';
import { BootSplashDismisser, BootSplashKeeper, UnsignedSignInRedirect } from './auth/AuthBootSplash';

/**
 * A protected route component that handles authentication and role-based access
 */
const ProtectedRoute = ({
  requiredRole = null,
  allowUnknownRole = false,
  redirectTo = "/sign-in"
}) => {
  const { isSignedIn, isLoaded, user } = useUser();
  const location = useLocation();

  // Show loading state while auth is initializing.
  // Only show dashboard skeleton if the stored token is not expired.
  // Expired tokens (e.g., left tab open overnight) show minimal loader —
  // auth will clear the stale session and redirect to /sign-in.
  if (!isLoaded) {
    if (hasValidStoredSession()) {
      return (
        <>
          <BootSplashDismisser ready />
          {isSoAppPath(location.pathname) ? <PackResolvingShell /> : <DashboardSkeleton />}
        </>
      );
    }

    return <BootSplashKeeper />;
  }

  if (!isSignedIn) {
    return <UnsignedSignInRedirect to={redirectTo} state={{ from: location }} />;
  }

  // If a specific role is required, check if user has it
  if (requiredRole && user) {
    const userRole = user?.role;

    // Handle array of allowed roles
    const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];

    // If user role is unknown and we don't allow unknown roles
    if (!userRole && !allowUnknownRole) {
      console.warn('User has no role assigned');
      return <Navigate to="/dashboard" replace />;
    }

    // Check if the user has one of the required roles
    if (userRole && !allowedRoles.includes(userRole)) {
      console.warn(`Access denied: Required role ${requiredRole}, but user has role ${userRole}`);
      return <Navigate to="/dashboard" replace />;
    }
  }

  // User is authenticated (and has required role if specified)
  return <Outlet />;
};

export default ProtectedRoute;
